export interface PricingDisplayContract {
  net_amount_sar: number;
  vat_rate_percent: number;
  vat_amount_sar: number;
  gross_amount_sar: number;
  gross_amount_halalas: number;
  vat_included: true;
}

export interface PlanPricingDisplay {
  monthly: PricingDisplayContract | null;
  yearly: PricingDisplayContract | null;
  intro: PricingDisplayContract | null;
}

export interface PricingDisplayCatalog {
  contract: 'customer_pricing_display.v1';
  platform: 'forkhire' | 'forkseek';
  tax_config_id: string;
  plans: Record<string, PlanPricingDisplay>;
  credit_packs: Record<string, PricingDisplayContract>;
  add_ons: Record<string, PricingDisplayContract>;
}

export interface PricingDisplayQuote {
  contract: 'customer_pricing_display.v1';
  pricing_contract_version?: 'customer_pricing_display.v1';
  tax_config_id?: string;
  quote_fingerprint?: string;
  display: PricingDisplayContract;
  renewal_display?: PricingDisplayContract;
  intro_display?: PricingDisplayContract | null;
  unit_display?: PricingDisplayContract;
  lines?: ReadonlyArray<{
    source_id: string;
    quantity: number;
    display: PricingDisplayContract;
    unit_display: PricingDisplayContract;
  }>;
}

export function isPricingDisplayContract(
  value: unknown,
): value is PricingDisplayContract {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<PricingDisplayContract>;
  const structurallyValid = candidate.vat_included === true &&
    Number.isFinite(candidate.net_amount_sar) &&
    Number(candidate.net_amount_sar) >= 0 &&
    Number.isFinite(candidate.vat_rate_percent) &&
    Number(candidate.vat_rate_percent) >= 0 &&
    Number.isFinite(candidate.vat_amount_sar) &&
    Number(candidate.vat_amount_sar) >= 0 &&
    Number.isFinite(candidate.gross_amount_sar) &&
    Number(candidate.gross_amount_sar) >= 0 &&
    Number.isInteger(candidate.gross_amount_halalas);
  if (!structurallyValid) return false;
  try {
    const invariant = assertNetVatGrossInvariant({
      netAmountSar: Number(candidate.net_amount_sar),
      vatAmountSar: Number(candidate.vat_amount_sar),
      grossAmountSar: Number(candidate.gross_amount_sar),
    });
    return invariant.grossAmountHalalas ===
      Number(candidate.gross_amount_halalas) &&
      sarToHalalas(
        Number(candidate.gross_amount_sar),
        'pricing_display_gross',
      ) === Number(candidate.gross_amount_halalas);
  } catch {
    return false;
  }
}

export function paymentFormMatchesPricingDisplay(
  pricing: PricingDisplayContract | null | undefined,
  paymentFormAmount: unknown,
): boolean {
  return isPricingDisplayContract(pricing) &&
    Number.isInteger(Number(paymentFormAmount)) &&
    pricing.gross_amount_halalas === Number(paymentFormAmount);
}

export function isPayablePricingDisplayContract(
  value: unknown,
): value is PricingDisplayContract {
  return isPricingDisplayContract(value) &&
    value.net_amount_sar > 0 &&
    value.gross_amount_sar > 0 &&
    value.gross_amount_halalas > 0;
}

export function planPricingForCycle(
  catalog: PricingDisplayCatalog | null | undefined,
  planId: string | null | undefined,
  cycle: 'monthly' | 'yearly',
): PricingDisplayContract | null {
  if (!planId) return null;
  const pricing = catalog?.plans?.[planId]?.[cycle];
  return isPayablePricingDisplayContract(pricing) ? pricing : null;
}

export function planIntroPricing(
  catalog: PricingDisplayCatalog | null | undefined,
  planId: string | null | undefined,
): PricingDisplayContract | null {
  if (!planId) return null;
  const pricing = catalog?.plans?.[planId]?.intro;
  return isPayablePricingDisplayContract(pricing) ? pricing : null;
}

export interface CreditCartPricingState {
  quote: PricingDisplayQuote | null;
  ready: boolean;
  reason:
    | 'empty'
    | 'loading'
    | 'request_failed'
    | 'missing_cart_total'
    | 'missing_line'
    | 'missing_unit'
    | 'missing_binding'
    | 'invalid_quantity'
    | null;
}

export function validateCreditCartPricingQuote(
  items: ReadonlyArray<{ packId: string; quantity: number }>,
  quote: PricingDisplayQuote | null | undefined,
  state: { isLoading?: boolean; isError?: boolean } = {},
): CreditCartPricingState {
  if (items.length === 0) return { quote: null, ready: false, reason: 'empty' };
  if (state.isLoading) return { quote: null, ready: false, reason: 'loading' };
  if (state.isError || !quote) return { quote: null, ready: false, reason: 'request_failed' };
  if (!isPayablePricingDisplayContract(quote.display)) {
    return { quote: null, ready: false, reason: 'missing_cart_total' };
  }
  if (
    quote.pricing_contract_version !== 'customer_pricing_display.v1' ||
    !quote.tax_config_id ||
    !quote.quote_fingerprint
  ) {
    return { quote: null, ready: false, reason: 'missing_binding' };
  }
  const lines = quote.lines ?? [];
  for (const item of items) {
    if (!Number.isInteger(item.quantity) || item.quantity <= 0) {
      return { quote: null, ready: false, reason: 'invalid_quantity' };
    }
    const matching = lines.filter((line) => line.source_id === item.packId);
    if (matching.length !== 1 || matching[0].quantity !== item.quantity) {
      return { quote: null, ready: false, reason: 'missing_line' };
    }
    if (!isPayablePricingDisplayContract(matching[0].unit_display)) {
      return { quote: null, ready: false, reason: 'missing_unit' };
    }
    if (!isPayablePricingDisplayContract(matching[0].display)) {
      return { quote: null, ready: false, reason: 'missing_line' };
    }
  }
  if (lines.length !== items.length) {
    return { quote: null, ready: false, reason: 'missing_line' };
  }
  return { quote, ready: true, reason: null };
}
import {
  assertNetVatGrossInvariant,
  sarToHalalas,
} from '@/lib/canonicalMoney';
